import React from 'react';

import {
  EMPLOYEE_CHANGE_LOG_CHANGE_TYPE,
  EmployeeChangeLog,
  EmployeeChangeLogChangeType,
} from '../../typescript-types/employee-change-log';

import { EmployeeChangeLogDetails } from './employee-change-log-details';

import { formatDateTimeShort } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';

type InputProps = {
  historyItem: EmployeeChangeLog;
  orgsInAccount: Array<Org>;
  orgUsers: Array<User>;
  employeeGroups: Array<EmployeeGroup>;
  employees: Array<Employee>;
  integrationName?: string | null;
};

const ChangeHistoryIcon = ({ changeLog }: { changeLog: EmployeeChangeLog }) => {
  if (
    (
      [
        EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.CREATE,
        EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.CREATED_IN_HRIS,
      ] as EmployeeChangeLogChangeType[]
    ).includes(changeLog.changeType)
  ) {
    return <MaterialDesignIcon iconName={ICON_NAMES.ADD} color="success" size="sm" />;
  }
  if (changeLog.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.ARCHIVE) {
    return <MaterialDesignIcon iconName={ICON_NAMES.DO_NOT_DISTURB} color="error" size="sm" />;
  }
  if (changeLog.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.MERGE) {
    return <MaterialDesignIcon iconName={ICON_NAMES.MERGE_TYPE} color="gray" size="sm" />;
  }
  return <MaterialDesignIcon iconName={ICON_NAMES.CHECK} color="gray" size="sm" />;
};

export const EmployeeHistoryItem = ({
  historyItem,
  orgsInAccount,
  orgUsers,
  employeeGroups,
  employees,
  integrationName,
}: InputProps) => {
  const displayUsername = () => {
    const userForHistoryItem =
      orgUsers.find((user) => user.username === historyItem.username) || null;

    if (historyItem.source === 'DIRECTORY_IMPORT') {
      return 'Directory Import';
    }
    if (historyItem.source === 'QUICKBOOKS') {
      return 'Quickbooks Integration';
    }
    if (historyItem.source === 'MERGE_DEV') {
      return integrationName || 'HR/payroll integration';
    }
    if (!userForHistoryItem) {
      return 'Team Engine';
    }

    return (
      `${userForHistoryItem?.firstName || ''} ${userForHistoryItem?.lastName || ''}`.trim() ||
      'Unknown user'
    );
  };

  const displayTitle = () => {
    if (historyItem.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.CREATE) {
      return 'Created';
    }
    if (historyItem.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.CREATED_IN_HRIS) {
      return `Created in ${integrationName || 'HR/payroll system'}`;
    }
    if (historyItem.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.ARCHIVE) {
      return 'Deleted';
    }
    if (historyItem.changeType === EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.MERGE) {
      return 'Combined with employee';
    }
    return 'Updated';
  };

  const anyAssociationChanges =
    historyItem.groupChanges?.newValue.length ||
    historyItem.groupChanges?.oldValue.length ||
    historyItem.supervisorChanges?.newValue.length ||
    historyItem.supervisorChanges?.oldValue.length ||
    historyItem.subordinateChanges?.newValue.length ||
    historyItem.subordinateChanges?.oldValue.length;

  return (
    <div className="u-padding u-display-flex">
      <div className="u-padding-right">
        <ChangeHistoryIcon changeLog={historyItem} />
      </div>

      <div className="u-flex-1">
        <span className="typo-body">{displayTitle()}</span>
        <div className="typo-subtitle">
          <div>Changed By: {displayUsername()}</div>
          <div>Changed On: {formatDateTimeShort(historyItem.updatedAt)}</div>
        </div>
      </div>

      {(
        [
          EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.UPDATE,
          EMPLOYEE_CHANGE_LOG_CHANGE_TYPE.MERGE,
        ] as EmployeeChangeLogChangeType[]
      ).includes(historyItem.changeType) ? (
        <div className="u-flex-1 typo-subtitle">
          <EmployeeChangeLogDetails
            employeeHistoryDetailClassName={anyAssociationChanges ? 'u-margin-bottom-md' : ''}
            changeHistoryId={historyItem.employeeChangeLogId}
            employeeChanges={historyItem.employeeChanges}
            orgsInAccount={orgsInAccount}
            personChanges={historyItem.personChanges}
            groupChanges={historyItem.groupChanges}
            employeeGroupsInOrg={employeeGroups}
            supervisorChanges={historyItem.supervisorChanges}
            subordinateChanges={historyItem.subordinateChanges}
            employeesInOrg={employees}
          />
        </div>
      ) : null}
    </div>
  );
};
